// src/pages/CompleteRegistration.jsx
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useLanguage } from "./LanguageContext";
import { api } from "../lib/http";

const API_BASE_URL = "https://shopaholics.ge/api";

/** Robust token reader: ითვალისწინებს ?token=..., #token=..., &amp; და პლიუსების/სპეისების თემა */
function readTokenFromUrl() {
  try {
    const href = String(window.location.href || "");
    const normalizedHref = href.replace(/&amp;/gi, "&");
    const m = normalizedHref.match(/[?&#]token=([^&#]+)/);
    let tok = m ? m[1] : "";
    if (!tok) return "";
    tok = decodeURIComponent(tok);
    tok = tok.replace(/\s+/g, "+").trim();
    return tok;
  } catch {
    return "";
  }
}

function syncLangInUrl(lang) {
  try {
    const url = new URL(window.location.href);
    if (lang && ["ka", "en"].includes(lang)) {
      if (url.searchParams.get("lang") !== lang) {
        url.searchParams.set("lang", lang);
        window.history.replaceState(null, "", url.toString());
      }
    }
    document.documentElement.setAttribute("lang", lang || "ka");
  } catch {}
}

function installRuntimeErrorOverlay(setErr) {
  const onErr = (e) => setErr(`JS error: ${e?.message || e}`);
  const onRej = (e) =>
    setErr(`Promise error: ${e?.reason?.message || e?.reason || e}`);
  window.addEventListener("error", onErr);
  window.addEventListener("unhandledrejection", onRej);
  return () => {
    window.removeEventListener("error", onErr);
    window.removeEventListener("unhandledrejection", onRej);
  };
}

export default function CompleteRegistration() {
  const { language, setLanguage, t } = useLanguage();

  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [birthDate, setBirthDate] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  // resend UI (expired/invalid link)
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState("");

  // Title
  useEffect(() => {
    document.title = t("titles.complete") || "Complete profile | Shopaholics";
  }, [language, t]);

  // On first load: read lang + token
  useEffect(() => {
    try {
      const urlLang = new URL(window.location.href).searchParams.get("lang");
      if (urlLang && ["ka", "en"].includes(urlLang) && urlLang !== language) {
        setLanguage(urlLang);
      }
    } catch {}

    setToken(readTokenFromUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync lang in URL + <html lang>
  useEffect(() => {
    syncLangInUrl(language);
  }, [language]);

  // runtime overlay
  useEffect(() => {
    return installRuntimeErrorOverlay(setError);
  }, []);

  const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

  const canSubmit = useMemo(() => {
    return (
      token &&
      passwordRegex.test(password) &&
      confirm === password &&
      /^\d{4}-\d{2}-\d{2}$/.test(birthDate)
    );
  }, [token, password, confirm, birthDate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResendMsg("");

    if (!token) return setError(t("login.link_expired_or_invalid") || "Invalid or expired link.");
    if (!passwordRegex.test(password)) return setError(t("miniregister.badPassword") || t("login.newpassword") || "Invalid password");
    if (password !== confirm) return setError(t("login.confirmpassword") ? (language === "en" ? "Passwords do not match" : "პაროლები არ ემთხვევა") : (language === "en" ? "Passwords do not match" : "პაროლები არ ემთხვევა"));
    if (!/^\d{4}-\d{2}-\d{2}$/.test(birthDate)) {
      return setError(language === "en" ? "Enter birth date in YYYY-MM-DD format." : "დაბ. თარიღი შეიყვანეთ ფორმატით YYYY-MM-DD.");
    }

    const safeToken = (token || "").replace(/\s+/g, "+").trim();
    if (!safeToken) return setError(t("login.link_expired_or_invalid") || "Invalid or expired link.");

    setSubmitting(true);
    try {
      const res = await api.post(
        `${API_BASE_URL}/complete_registration.php`,
        {
          token: safeToken,
          password,
          password_confirm: confirm,
          birth_date: birthDate,
          lang: ["ka", "en"].includes(language) ? language : "ka",
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (res.data?.success) setDone(true);
      else setError(res.data?.error || (language === "en" ? "Unknown error. Please try again." : "უცნობი შეცდომა. სცადეთ ხელახლა."));
    } catch (err) {
      console.error(err);
      setError(language === "en" ? "Network error. Please try again." : "ქსელური შეცდომა. სცადეთ ხელახლა.");
    } finally {
      setSubmitting(false);
    }
  };

  const onResend = async (e) => {
    e.preventDefault();
    setError("");
    setResendMsg("");

    const em = (resendEmail || "").trim();
    if (!em || !/^\S+@\S+\.\S+$/.test(em)) {
      setError(t("login.invalid_email") || (language === "en" ? "Invalid email" : "არასწორი ელ.ფოსტა"));
      return;
    }

    setResendLoading(true);
    try {
      const res = await api.post(
        `${API_BASE_URL}/resend_complete_registration.php`,
        {
          email: em,
          lang: ["ka", "en"].includes(language) ? language : "ka",
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (res.data?.success) {
        if (res.data?.action === "ALREADY_ACTIVE") {
          setResendMsg(res.data?.message || t("login.account_already_active"));
        } else {
          setResendMsg(res.data?.message || t("login.resend_complete_sent"));
        }
      } else {
        setError(res.data?.error || (language === "en" ? "Unknown error. Please try again." : "უცნობი შეცდომა. სცადეთ ხელახლა."));
      }
    } catch (err) {
      console.error(err);
      setError(language === "en" ? "Network error. Please try again." : "ქსელური შეცდომა. სცადეთ ხელახლა.");
    } finally {
      setResendLoading(false);
    }
  };

  // ✅ Invalid/Expired link UI + resend
  if (!token && !done) {
    return (
      <Wrap>
        <Card as="form" onSubmit={onResend}>
          <Title>{t("titles.complete") || (language === "en" ? "Complete profile" : "დაასრულე პროფილი")}</Title>

          <Error>{t("login.link_expired_or_invalid") || (language === "en" ? "Invalid or expired link." : "ბმული არასწორია ან ვადაგასულია.")}</Error>

          <Hint>
            <strong>{t("login.resend_complete_title")}</strong>
            <div style={{ marginTop: 6 }}>{t("login.resend_complete_hint")}</div>
          </Hint>

          <Label>{t("login.email")}</Label>
          <Input
            type="email"
            value={resendEmail}
            onChange={(e) => setResendEmail(e.target.value)}
            placeholder="name@example.com"
          />

          {resendMsg ? <Success>{resendMsg}</Success> : null}
          {error ? <Error>{error}</Error> : null}

          <Actions>
            <Btn type="submit" disabled={resendLoading}>
              {resendLoading ? (t("login.sending") || (language === "en" ? "Sending..." : "გაგზავნა...")) : t("login.resend_complete_btn")}
            </Btn>
            <BtnSecondary as="a" href="/login">
              {t("login.signin") || (language === "en" ? "Sign in" : "ავტორიზაცია")}
            </BtnSecondary>
          </Actions>
        </Card>
      </Wrap>
    );
  }

  if (done) {
    return (
      <Wrap>
        <Card>
          <Title>{language === "en" ? "Registration Completed ✅" : "რეგისტრაცია დასრულდა ✅"}</Title>
          <p style={{ color: "#555", marginTop: 0 }}>
            {language === "en"
              ? "Your account has been successfully created. You can log in or continue shopping."
              : "თქვენი ანგარიში შეიქმნა წარმატებით. შეგიძლიათ შეხვიდეთ ანგარიშში ან გააგრძელოთ ყიდვა."}
          </p>
          <Actions>
            <Btn as="a" href="/login">
              {t("login.signin") || (language === "en" ? "Sign in" : "ავტორიზაცია")}
            </Btn>
            <BtnSecondary as="a" href="/">
              {language === "en" ? "Back to Home" : "მთავარზე დაბრუნება"}
            </BtnSecondary>
          </Actions>
        </Card>
      </Wrap>
    );
  }

  // ✅ Normal complete form
  return (
    <Wrap>
      <Card as="form" onSubmit={onSubmit}>
        <Title>{t("titles.complete") || (language === "en" ? "Complete profile" : "დაასრულე პროფილი")}</Title>

        <p style={{ color: "#666", marginTop: 0 }}>
          {language === "en"
            ? "Password must contain an uppercase letter, a number, a symbol, and be at least 8 characters long."
            : "პაროლი უნდა შეიცავდეს დიდ ასოს, ციფრს, სიმბოლოს და იყოს მინიმუმ 8 სიმბოლო."}
        </p>

        <Label>{t("login.password") || (language === "en" ? "Password" : "პაროლი")}</Label>
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={language === "en" ? "Min. 8 characters" : "მინ. 8 სიმბოლო"}
        />

        <Label>{t("login.confirmpassword") || (language === "en" ? "Confirm password" : "დაადასტურე პაროლი")}</Label>
        <Input
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />

        <Label>{t("login.birthdate") || (language === "en" ? "Birthdate" : "დაბადების თარიღი")}</Label>
        <Input
          type="date"
          value={birthDate}
          onChange={(e) => setBirthDate(e.target.value)}
        />

        {error ? <Error>{error}</Error> : null}

        <Actions>
          <Btn type="submit" disabled={!canSubmit || submitting}>
            {submitting ? (t("login.sending") || (language === "en" ? "Sending..." : "გაგზავნა...")) : (language === "en" ? "Finish registration" : "დაასრულე რეგისტრაცია")}
          </Btn>
          <BtnSecondary as="a" href="/">
            {language === "en" ? "Cancel" : "გაუქმება"}
          </BtnSecondary>
        </Actions>
      </Card>
    </Wrap>
  );
}

/* ================= styled ================= */
const Wrap = styled.div`
  min-height: 60vh;
  display: grid;
  place-items: center;
  padding: 24px;
  background: #fafafa;
`;

const Card = styled.div`
  width: 100%;
  max-width: 460px;
  background: #fff;
  border-radius: 14px;
  padding: 20px 22px;
  box-shadow: 0 10px 30px rgba(0,0,0,.08);
`;

const Title = styled.h2`
  margin: 0 0 10px 0;
`;

const Hint = styled.div`
  margin-top: 10px;
  color: #555;
  line-height: 1.4;
`;

const Label = styled.label`
  display: block;
  font-weight: 800;
  margin-top: 12px;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 95%;
  font-size: 15px;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  outline: none;
  &:focus { border-color: #333; }
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const Btn = styled.button`
  background: #111;
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 900;
  cursor: pointer;
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

const BtnSecondary = styled(Btn)`
  background: #f3f3f3;
  color: #111;
`;

const Error = styled.div`
  margin-top: 10px;
  color: #b00020;
  font-weight: 800;
`;

const Success = styled.div`
  margin-top: 10px;
  color: #0b7a2a;
  font-weight: 800;
`;